// components/ChatMessage.js
import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import toast from 'react-hot-toast'
import { useCompany } from '../contexts/CompanyContext'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import PersonIcon from '@mui/icons-material/Person'
import InfoIcon from '@mui/icons-material/Info'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LanguageIcon from '@mui/icons-material/Language'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import TableChartIcon from '@mui/icons-material/TableChart'
import PsychologyIcon from '@mui/icons-material/Psychology'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ImageGallery from './ImageGallery'
import PDFPreviewModal from './PDFPreviewModal'
import MarkdownWithImageReferences from './MarkdownWithImageReferences'
import { getImageReferenceName, normalizeUmlauts } from '../utils/imageMentions'

import './ChatMessage.css'

function ChatMessage ({
  message,
  conversationId,
  messageIndex,
  onFeedback,
  onKnowledgeClick
}) {
  const { botName } = useCompany()
  const [feedback, setFeedback] = useState(message.feedback || null)
  const [pdfModalSource, setPdfModalSource] = useState(null)
  const isBot = message.type === 'bot' || message.type === 'assistant'
  const isUser = message.type === 'user'
  const isSystem = message.type === 'system'


  // Einheitlicher Handler für alle Source-Klicks
  const handleSourceClick = source => {
    // Techniker-Wissen: Öffne Modal (wenn Callback vorhanden) oder Navigate zu Browse-Seite
    if (source.type === 'technician_knowledge') {
      if (!source.knowledgeId) {
        console.error('No knowledgeId provided')
        return
      }

      // Wenn onKnowledgeClick Callback vorhanden, verwende diesen
      if (onKnowledgeClick) {
        onKnowledgeClick(source.knowledgeId)
      } else {
        // Fallback: Öffne Browse-Seite in neuem Tab (für andere Verwendungen)
        const browseUrl = `/browse/${source.knowledgeId}`
        window.open(browseUrl, '_blank')
      }

      // Optional: Analytics tracken
      if (typeof analyticsService !== 'undefined') {
        analyticsService.trackUserAction('knowledge_browsed', {
          knowledge_id: source.knowledgeId
        })
      }
    }
    // Service Manual: Öffne PDF Preview Modal
    else {
      if (!source.filePath) {
        console.error('No filePath in source:', source)
        toast.error('Fehler: PDF-Pfad nicht gefunden')
        return
      }

      setPdfModalSource(source)

      // Optional: Analytics tracken
      if (typeof analyticsService !== 'undefined') {
        analyticsService.trackUserAction('pdf_preview_opened', {
          file_name: source.fileName,
          file_path: source.filePath,
          device_name: source.deviceName,
          page_number: source.pageNumber
        })
      }
    }
  }

  // Handler für Feedback
  const handleFeedback = async type => {
    if (feedback) return // Already submitted

    setFeedback(type)

    // Call parent callback if provided
    if (onFeedback) {
      try {
        await onFeedback({
          conversationId,
          messageIndex,
          feedbackType: type,
          message: message.content,
          timestamp: new Date().toISOString()
        })
      } catch (error) {
        console.error('Error submitting feedback:', error)
        // Optionally revert feedback state on error
        setFeedback(null)
      }
    }
  }

  // Helper function to extract filename from Firebase Storage URL
  const extractFilenameFromUrl = (url) => {
    try {
      // Firebase Storage URL format: .../o/path%2Fto%2Ffilename.ext?alt=media...
      const match = url.match(/\/o\/([^?]+)/)
      if (match && match[1]) {
        // Decode URL encoding
        const decodedPath = decodeURIComponent(match[1])
        // Get filename (last part after /)
        const parts = decodedPath.split('/')
        return parts[parts.length - 1]
      }
    } catch (e) {
      console.error('Error extracting filename from URL:', e)
    }
    return null
  }

  // Sammle alle Bilder aus allen Sources
  const allImages = []
  if (message.sources && message.sources.length > 0) {
    message.sources.forEach(source => {
      if (source.images && source.images.length > 0) {


        // Transformiere images array - falls es Strings sind, mache Objekte daraus
        source.images.forEach((img, idx) => {
          let imageUrl = null
          let filename = null
          let referenceName = null

          // Verschiedene Formate behandeln
          if (typeof img === 'string') {
            imageUrl = img
            // Try to extract filename from URL first
            filename = extractFilenameFromUrl(img) || `${source.fileName || 'Quelle'}_Bild_${idx + 1}`
          } else if (img && typeof img === 'object') {
            imageUrl = img.url || img.imageUrl || img.downloadURL || null
            // Try multiple sources for filename (prioritize filename over name)
            let rawFilename = img.filename || img.name

            // If no filename property, try to extract from URL
            if (!rawFilename && imageUrl) {
              rawFilename = extractFilenameFromUrl(imageUrl)
            }

            // Fallback to constructed name
            if (!rawFilename) {
              rawFilename = `${source.fileName || 'Quelle'}_Bild_${idx + 1}`
            }

            // WICHTIG: Normalize filename (convert umlauts to ASCII) before using it
            filename = normalizeUmlauts(rawFilename)

            // WICHTIG: referenceName hat IMMER Priorität - verwende ihn wenn vorhanden
            // Nur wenn er wirklich fehlt (undefined/null), generiere ihn aus filename
            // Dies ist wichtig, weil der referenceName vom User geändert werden kann (z.B. in Step2Solution.js)
            // und dann muss der geänderte referenceName verwendet werden, nicht der aus filename generierte
            referenceName = (img.referenceName !== undefined && img.referenceName !== null) 
              ? img.referenceName 
              : null
          }

          if (imageUrl) {
            // WICHTIG: Ensure referenceName exists - generate from normalized filename ONLY if not present
            // Dies gilt für Bilder UND Videos - beide müssen referenceName haben
            // ABER: Nur wenn referenceName wirklich fehlt (null), nicht wenn er nur anders ist als filename
            if (referenceName === null && filename) {
              referenceName = getImageReferenceName(filename)
            }

            // Detect media type from URL or filename
            const isVideo = imageUrl.match(/\.(mp4|webm|mov)$/i) || filename.match(/\.(mp4|webm|mov)$/i)
            const mediaType = isVideo ? 'video' : (img?.mediaType || 'image')

            allImages.push({
              url: imageUrl,
              filename: filename,
              name: filename, // Ensure name is also set for consistency
              referenceName: referenceName, // WICHTIG: referenceName muss immer gesetzt sein
              mediaType: mediaType
            })
          }
        })
      }
    })
  }


  return (
    <>
      {/* Bilder ÜBER der Message anzeigen */}
      {isBot && allImages.length > 0 && (
        <div className='message-images-preview'>
          <ImageGallery images={allImages} compact={true} maxPreview={3} />
        </div>
      )}

      <div className={`message ${message.type}`}>
        <div className='message-header'>
          <span className='message-role'>
            {isBot && (
              <>
                <SmartToyIcon
                  fontSize='small'
                  style={{ verticalAlign: 'middle', marginRight: '0.35rem' }}
                />
                {botName}
              </>
            )}
            {isUser && (
              <>
                <PersonIcon
                  fontSize='small'
                  style={{ verticalAlign: 'middle', marginRight: '0.35rem' }}
                />
                Sie
              </>
            )}
            {isSystem && (
              <>
                <InfoIcon
                  fontSize='small'
                  style={{ verticalAlign: 'middle', marginRight: '0.35rem' }}
                />
                System
              </>
            )}
          </span>
          <span className='message-time'>
            {new Date(message.timestamp).toLocaleTimeString('de-DE', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        </div>
        <div className='message-content'>
          {isBot ? (
            message.content ? (
              <MarkdownWithImageReferences
                content={message.content}
                images={allImages}
              />
            ) : (
              <p className='md-paragraph'>Keine Antwort verfügbar.</p>
            )
          ) : (
            <p>{message.content || ''}</p>
          )}
        </div>

        {/* Feedback Buttons - nur für Bot-Nachrichten */}
        {isBot && (
          <div className='message-feedback'>
            <button
              className={`feedback-btn ${
                feedback === 'positive' ? 'active positive' : ''
              }`}
              onClick={() => handleFeedback('positive')}
              disabled={!!feedback}
              title='Hilfreich'
            >
              <ThumbUpIcon fontSize='small' />
            </button>
            <button
              className={`feedback-btn ${
                feedback === 'negative' ? 'active negative' : ''
              }`}
              onClick={() => handleFeedback('negative')}
              disabled={!!feedback}
              title='Nicht hilfreich'
            >
              <ThumbDownIcon fontSize='small' />
            </button>
            {feedback && (
              <span className='feedback-thanks'>Danke für Ihr Feedback!</span>
            )}
          </div>
        )}

        {message.sources && message.sources.length > 0 && (
          <div className='message-sources'>
            <details>
              <summary>
                <MenuBookIcon
                  fontSize='small'
                  style={{ verticalAlign: 'middle', marginRight: '0.35rem' }}
                />
                Quellen ({message.sources.length})
              </summary>
              <div className='sources-list'>
                {message.sources.map((source, idx) => (
                  <div
                    key={idx}
                    className={`source-item ${
                      source.type === 'technician_knowledge'
                        ? 'technician-source'
                        : 'manual-source'
                    } clickable`}
                    onClick={() => handleSourceClick(source)}
                    role='button'
                    tabIndex={0}
                    onKeyPress={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleSourceClick(source)
                      }
                    }}
                  >
                    <div className='source-header'>
                      <div className='source-info'>
                        <span className='source-file'>{source.fileName}</span>
                        {source.pageNumber && (
                          <span className='source-page'>
                            Seite {source.pageNumber}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Techniker-Wissen Details */}
                    {source.type === 'technician_knowledge' && (
                      <div className='technician-meta'>
                        <span className='tech-submitter'>
                          <PersonIcon
                            fontSize='inherit'
                            style={{
                              verticalAlign: 'middle',
                              marginRight: '0.25rem'
                            }}
                          />
                          {source.submittedBy}
                        </span>
                        {source.difficulty && (
                          <span
                            className={`tech-difficulty ${source.difficulty}`}
                          >
                            {source.difficulty}
                          </span>
                        )}
                        {source.estimatedTime && (
                          <span className='tech-time'>
                            <AccessTimeIcon
                              fontSize='inherit'
                              style={{
                                verticalAlign: 'middle',
                                marginRight: '0.25rem'
                              }}
                            />
                            {source.estimatedTime}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Service Manual Details */}
                    {source.errorCode && (
                      <div className='source-meta'>
                        <span className='source-code'>
                          Code: {source.errorCode}
                        </span>
                        {source.validationStatus &&
                          source.validationStatus !== 'technician_approved' && (
                            <span
                              className={`validation-badge ${source.validationStatus}`}
                            >
                              {source.validationStatus ===
                                'cross_validated' && (
                                <>
                                  <CheckCircleIcon
                                    fontSize='inherit'
                                    style={{
                                      verticalAlign: 'middle',
                                      marginRight: '0.25rem'
                                    }}
                                  />
                                  Validiert
                                </>
                              )}
                              {source.validationStatus === 'table_only' && (
                                <>
                                  <TableChartIcon
                                    fontSize='inherit'
                                    style={{
                                      verticalAlign: 'middle',
                                      marginRight: '0.25rem'
                                    }}
                                  />
                                  Tabelle
                                </>
                              )}
                              {source.validationStatus === 'llm_only' && (
                                <>
                                  <PsychologyIcon
                                    fontSize='inherit'
                                    style={{
                                      verticalAlign: 'middle',
                                      marginRight: '0.25rem'
                                    }}
                                  />
                                  KI-Extraktion
                                </>
                              )}
                            </span>
                          )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
        {message.metadata?.translationInfo?.wasTranslated && (
          <div className='translation-info'>
            <LanguageIcon
              fontSize='small'
              style={{ verticalAlign: 'middle', marginRight: '0.35rem' }}
            />
            Übersetzt von Deutsch
          </div>
        )}
      </div>

      {/* PDF Preview Modal */}
      {pdfModalSource && (
        <PDFPreviewModal
          source={pdfModalSource}
          onClose={() => setPdfModalSource(null)}
        />
      )}
    </>
  )
}

export default ChatMessage
